import { useSmsTemplates } from './useSmsTemplates';
import { SmsTemplateType, DEFAULT_SMS_TEMPLATES } from './smsTemplates';
import {
  SmsPlaceholderData,
  formatClientName,
  formatServicesForSms,
  formatDateOnlyForSms,
  formatTimeOnlyForSms,
  renderSmsTemplate,
  formatDepositAmount,
} from './smsTemplateUtils';
import { Service } from '@features/service/entity';
import { useFormatCurrency } from '@features/currency/currency.hooks';

/**
 * Hook pro generování SMS zpráv z šablon
 */
export const useRenderSmsFromTemplate = () => {
  const { data } = useSmsTemplates();
  const { format: formatCurrency } = useFormatCurrency();

  /**
   * Najde šablonu uživatele nebo vrátí výchozí
   */
  const getTemplate = (type: SmsTemplateType): string => {
    const userTemplate = data?.templates.find(t => t.type === type);
    return userTemplate?.content || DEFAULT_SMS_TEMPLATES[type];
  };

  /**
   * Vygeneruje SMS potvrzení schůzky
   */
  const renderAppointmentConfirmation = (params: {
    lastName: string;
    services: Service[];
    date: Date;
  }): string => {
    const template = getTemplate(SmsTemplateType.APPOINTMENT_REMINDER);

    const placeholderData: SmsPlaceholderData = {
      clientName: formatClientName(params.lastName),
      serviceName: formatServicesForSms(params.services),
      appointmentDate: formatDateOnlyForSms(params.date),
      appointmentTime: formatTimeOnlyForSms(params.date),
    };

    return renderSmsTemplate(template, placeholderData);
  };

  /**
   * Vygeneruje SMS připomenutí schůzky
   */
  const renderAppointmentReminder = (params: {
    lastName: string;
    services: Service[];
    date: Date;
  }): string => {
    const template = getTemplate(SmsTemplateType.APPOINTMENT_REMINDER);

    const placeholderData: SmsPlaceholderData = {
      clientName: formatClientName(params.lastName),
      serviceName: formatServicesForSms(params.services),
      appointmentDate: formatDateOnlyForSms(params.date), // Pouze datum
      appointmentTime: formatTimeOnlyForSms(params.date), // Pouze čas
    };

    return renderSmsTemplate(template, placeholderData);
  };

  /**
   * Vygeneruje SMS připomenutí zálohy
   */
  const renderDepositReminder = (params: {
    lastName: string;
    services: Service[];
    date: Date;
    depositAmount?: number;
    bankAccount?: string;
  }): string => {
    const template = getTemplate(SmsTemplateType.DEPOSIT_REMINDER);

    const placeholderData: SmsPlaceholderData = {
      clientName: formatClientName(params.lastName),
      serviceName: formatServicesForSms(params.services),
      depositAmount: formatDepositAmount(formatCurrency(params.depositAmount)),
      appointmentDate: formatDateOnlyForSms(params.date),
      appointmentTime: formatTimeOnlyForSms(params.date),
      bankAccount: params.bankAccount || 'CHYBÍ ČÍSLO ÚČTU',
    };

    return renderSmsTemplate(template, placeholderData);
  };

  /**
   * Vygeneruje SMS žádost o recenzi
   */
  const renderReviewRequest = (params: {
    lastName: string;
    reviewUrl?: string;
    services: Service[];
    date: Date;
  }): string => {
    const template = getTemplate(SmsTemplateType.REVIEW_REQUEST);

    const placeholderData: SmsPlaceholderData = {
      clientName: formatClientName(params.lastName),
      serviceName: formatServicesForSms(params.services),
      reviewUrl: params.reviewUrl || 'CHYBÍ ODKAZ NA RECENZI',
      appointmentDate: formatDateOnlyForSms(params.date),
      appointmentTime: formatTimeOnlyForSms(params.date),
    };

    return renderSmsTemplate(template, placeholderData);
  };

  return {
    renderAppointmentConfirmation,
    renderAppointmentReminder,
    renderDepositReminder,
    renderReviewRequest,
  };
};
